// Library methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

// MUI Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import SearchBox from "../../../../components/UI/SearchBox";
import CustomDatePicker from "../../../../components/UI/CustomDatePIcker";
import DefaultModal from "../../../../components/UI/DefaultModal";
import Alert from "../../../../components/UI/Alert";
import { WarningIcon } from "../../../../assets/icons";

// Styles
import {
  CustomTextFieldOutlined,
  CustomTextFieldOutlinedInputProps,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../../styles/muiStylesHelper";

// Utils
import { getLocale } from "../../../../utils/localeHelper";
import { ADMIN_TIMEZONE } from "../../../../enums";

const EditDeviceModal = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  clinicsOptions = [],
  deviceClinic,
  deviceAlias,
  setDeviceClinic,
  setDeviceAlias,
  checkClinicExams,
  deviceExpiryDate,
  clinicAssignmentExpiryDate,
  setDeviceExpiryDate,
  setClinicAssignmentExpiryDate,
  setUniversalDeviceId,
  universalDeviceId,
}) => {
  // internationalization
  const { t } = useTranslation();

  const shouldDisable = useMemo(
    () =>
      (deviceClinic?.name &&
        !clinicsOptions.some(
          (option) => option?.name === deviceClinic?.name
        )) ||
      clinicsOptions.length === 0 ||
      deviceAlias === "",
    [clinicsOptions, deviceAlias, deviceClinic?.name]
  );
  const handleChangeClinicAssignment = useCallback(
    (newValue) => {
      // Set the new value to the correct timezone and start of the day
      const newValueInZone = newValue?.setZone(ADMIN_TIMEZONE)?.startOf("day");
      setClinicAssignmentExpiryDate(newValueInZone);
    },
    [setClinicAssignmentExpiryDate]
  );
  const handleChangeDeviceExp = useCallback(
    (newValue) => {
      // Set the new value to the correct timezone and start of the day
      const newValueInZone = newValue?.setZone(ADMIN_TIMEZONE)?.startOf("day");
      setDeviceExpiryDate(newValueInZone);
    },
    [setDeviceExpiryDate]
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={getLocale(i18next.language).locale}
    >
      <DefaultModal
        isOpen={open}
        onClose={onClose}
        title={t("device_edit_modal_title")}
        maxWidth="xs"
        modalActions={
          <Grid
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
            }}
          >
            <Button variant="text" sx={ModalCustomCancel} onClick={onCancel}>
              {t("button_cancel")}
            </Button>
            {checkClinicExams() ? (
              <Button
                variant="outlined"
                sx={ModalCustomConfirm}
                disabled={clinicsOptions.length === 0 || deviceAlias === ""}
                onClick={onConfirm}
                color="error"
              >
                <WarningIcon />
                <Typography sx={{ ml: "8px" }}>
                  {t("button_confirm")}
                </Typography>
              </Button>
            ) : (
              <Button
                variant="outlined"
                sx={ModalCustomConfirm}
                disabled={shouldDisable}
                onClick={onConfirm}
              >
                {t("button_edit")}
              </Button>
            )}
          </Grid>
        }
      >
        <Grid display="flex" flexDirection="column" p="20px" gap="20px">
          {checkClinicExams() && (
            <Alert
              variant="warning"
              alertContent={t("device_edit_modal_confirmation")}
            />
          )}

          {/* device name field */}
          <TextField
            id="outlined-basic"
            value={deviceAlias}
            onChange={(e) => setDeviceAlias(e.target.value)}
            sx={() => CustomTextFieldOutlined({ mt: 0, mb: 0 })}
            label={t("device_modal_device_name")}
            InputProps={{
              sx: CustomTextFieldOutlinedInputProps,
            }}
            variant="outlined"
            fullWidth
            color="warning"
          />
          {/* clinic selector */}
          <SearchBox
            data={clinicsOptions}
            label={t("device_edit_modal_clinic")}
            state={deviceClinic}
            setState={setDeviceClinic}
          />
          <CustomDatePicker
            date={deviceExpiryDate}
            labelTitle="device_modal_device_expiry_date"
            handleChange={handleChangeDeviceExp}
            clinicTimezone={ADMIN_TIMEZONE}
          />
          <CustomDatePicker
            date={clinicAssignmentExpiryDate}
            labelTitle="device_modal_clinic_assignment_expiry_date"
            handleChange={handleChangeClinicAssignment}
            shouldDisable={!deviceClinic || !deviceClinic?.id}
            clinicTimezone={ADMIN_TIMEZONE}
          />

          <TextField
            id="outlined-basic"
            value={universalDeviceId}
            onChange={(e) => setUniversalDeviceId(e.target.value)}
            sx={() => CustomTextFieldOutlined({ mt: 0, mb: 0 })}
            label={t("device_modal_universalDeviceId")}
            InputProps={{
              sx: CustomTextFieldOutlinedInputProps,
            }}
            variant="outlined"
            fullWidth
            color="warning"
            shouldDisable={!deviceClinic || !deviceClinic?.id}
          />
        </Grid>
      </DefaultModal>
    </LocalizationProvider>
  );
};

export default EditDeviceModal;
